'use client';

import Link from 'next/link';
import dynamic from 'next/dynamic';
import { usePostHog } from 'posthog-js/react';
import { withCrossDomainDistinctId } from '@/lib/posthog-cross-domain';

const BrandCubeMark = dynamic(() => import('./BrandCubeMark'), {
  ssr: false,
  loading: () => <div className="w-10 h-10" />,
});

// Product links absolutize to https://votelyquiz.com because Footer
// renders on the marketing pages (/welcome, /test, /report, /group-quiz,
// /ideologies), and the marketing host (tryvotely.com) has no quiz
// surface — see proxy.ts which 404s these same paths on that host.
// "All Ideologies" stays relative since /ideologies is marketing and
// lives on both hosts.
const QUIZ_LINKS = [
  { label: 'Short Quiz (12 Qs)', href: 'https://votelyquiz.com/quiz?type=short' },
  { label: 'Long Quiz (60 Qs)', href: 'https://votelyquiz.com/quiz?type=long' },
  { label: 'Group Quiz', href: 'https://votelyquiz.com/group/new' },
  { label: 'Results History', href: 'https://votelyquiz.com/results/history' },
];

const LEARN_LINKS = [
  { label: 'All Ideologies', href: '/ideologies' },
  { label: 'Sample Report', href: 'https://votelyquiz.com/quiz/results/report?sample=1' },
  { label: 'My Account', href: 'https://votelyquiz.com/account' },
];

// Mobile link classes: inline-block keeps a wrapped label ("Short Quiz
// (12 Qs)" on a 320px phone) one continuous tap target instead of two line
// boxes with a dead gap, and the padding/negative margin pair grows the touch
// target to 32px tall without moving anything.
const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms of Service', href: '/terms' },
];

export default function Footer() {
  const posthog = usePostHog();
  // Click handler that, when the user is on tryvotely.com and the link
  // points at the product host, forwards the current PostHog distinct_id
  // so prior marketing-side activity stitches to the buyer in PostHog.
  const handleProductLinkClick = (href: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!href.startsWith('https://votelyquiz.com')) return;
    const target = withCrossDomainDistinctId(href, posthog);
    if (target !== href) {
      e.preventDefault();
      window.location.assign(target);
    }
  };
  return (
    <footer className="bg-surface-dark text-white font-inter">
      <div className="mx-auto px-5 lg:px-8 py-14" style={{ maxWidth: 'var(--max-w-landing)' }}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <BrandCubeMark size={40} />
              <Link href="/" className="text-lg font-semibold tracking-tight">Votely</Link>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              A political quiz that names the ideology closest to your views and explains where it comes from.
            </p>
          </div>

          {/* Quiz */}
          <div>
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Quiz</h3>
            <ul className="space-y-2.5">
              {QUIZ_LINKS.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} onClick={handleProductLinkClick(link.href)} className="text-sm text-slate-400 hover:text-white transition-colors max-md:inline-block max-md:py-1.5 max-md:-my-1.5">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Learn */}
          <div>
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Learn</h3>
            <ul className="space-y-2.5">
              {LEARN_LINKS.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} onClick={handleProductLinkClick(link.href)} className="text-sm text-slate-400 hover:text-white transition-colors max-md:inline-block max-md:py-1.5 max-md:-my-1.5">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Legal</h3>
            <ul className="space-y-2.5">
              {LEGAL_LINKS.map((link) => (
                <li key={link.href}>
                  <Link href={link.href} onClick={handleProductLinkClick(link.href)} className="text-sm text-slate-400 hover:text-white transition-colors max-md:inline-block max-md:py-1.5 max-md:-my-1.5">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            &copy; {new Date().getFullYear()} Votely. All rights reserved.
          </p>
          <p className="text-xs text-slate-500">
            Created by{' '}
            <a href="https://juleslemee.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
              Jules Lemée
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
